// frontend/app/components/lessonPlans/LessonPwoPlayer.js
//
// A self-contained WebGL PWO player for lessons.
//
// The tile flow (`handlePWOClick`) cannot be reused here. It is bound to the
// Learning module's lifecycle: it flips `activeModule`, shares one global
// iframe through `pwoKeepAlive`, and is torn down by the navigation-away
// cleanup effect keyed on `activeModule`. Inside a lesson that shows up as the
// scene appearing for a moment and then vanishing.
//
// This component owns its own iframe and talks to Unity directly. It reads no
// global PWO state and nothing else can close it:
//
//   1. GET /api/get_pwo_arguments_webgl -> { webgl_arguments }
//   2. iframe.src = <WEBGL_BASE_URL>/webgl/index.html
//   3. wait for postMessage { type: "WEBGL_READY" }
//   4. postMessage { type: "CALL_UNITY_FUNCTION", value: webgl_arguments }
//
// Switching scenes inside the same PWO skips steps 2-3 and re-sends step 4,
// which is the fast path a lesson walking several scenes depends on.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";

import { FASTAPI_BASE_URL, WEBGL_BASE_URL } from "../../config";

const WEBGL_ENTRY_PATH = "/webgl/index.html";
//: Native Unity canvas; the iframe is scaled to fit the container from this.
const NATIVE_WIDTH = 1280;
const NATIVE_HEIGHT = 720;
//: Unity needs a beat with a blank document before a clean boot.
const BOOT_DELAY_MS = 200;

const LessonPwoPlayer = ({
  sourcePwoId,
  sceneName,
  authToken,
  localLanguage = "en",
  isDarkMode = false,
  onClose,
  t,
}) => {
  const translate = t || ((value) => value);

  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const readyRef = useRef(false);
  const bootedPwoRef = useRef(null);
  const bootTimeoutRef = useRef(null);
  const readyHandlerRef = useRef(null);
  const cancelledRef = useRef(false);

  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const sendToUnity = useCallback((message) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(message, "*");
      return true;
    } catch (error) {
      console.warn("[LESSON PWO] postMessage failed:", error?.message);
      return false;
    }
  }, []);

  const teardown = useCallback(() => {
    if (bootTimeoutRef.current) {
      clearTimeout(bootTimeoutRef.current);
      bootTimeoutRef.current = null;
    }
    if (readyHandlerRef.current) {
      window.removeEventListener("message", readyHandlerRef.current);
      readyHandlerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!sourcePwoId || !sceneName) return undefined;

    cancelledRef.current = false;
    let disposed = false;

    (async () => {
      setStatus("loading");
      setErrorMessage(null);

      // ---- 1. WebGL arguments for this exact scene ----------------------
      let webglArguments = null;
      try {
        const response = await axios.get(
          `${FASTAPI_BASE_URL}/get_pwo_arguments_webgl`,
          {
            params: {
              pwo_id: sourcePwoId,
              scene_name: sceneName,
              auth_token: authToken,
              local_language: localLanguage,
            },
          }
        );
        webglArguments = response?.data?.webgl_arguments || null;
        if (!webglArguments) {
          throw new Error(
            response?.data?.error || "No WebGL arguments returned for this scene."
          );
        }
      } catch (error) {
        if (disposed) return;
        console.error("[LESSON PWO] argument fetch failed:", error);
        setStatus("error");
        setErrorMessage(
          error?.response?.data?.error ||
            error?.message ||
            "Could not load this 3D scene."
        );
        return;
      }

      if (disposed) return;

      // ---- 2. Hot path: same PWO already booted and ready ---------------
      const isSamePwo = bootedPwoRef.current === String(sourcePwoId);
      if (isSamePwo && readyRef.current) {
        sendToUnity({ type: "CALL_UNITY_FUNCTION", value: webglArguments });
        sendToUnity({ type: "APP_RESUME" });
        setStatus("ready");
        return;
      }

      // ---- 3. Cold boot -------------------------------------------------
      teardown();
      readyRef.current = false;

      const onMessage = (event) => {
        if (event.data?.type !== "WEBGL_READY") return;
        if (disposed) return;
        readyRef.current = true;
        bootedPwoRef.current = String(sourcePwoId);
        sendToUnity({ type: "CALL_UNITY_FUNCTION", value: webglArguments });
        sendToUnity({ type: "APP_RESUME" });
        setStatus("ready");
        window.removeEventListener("message", onMessage);
        readyHandlerRef.current = null;
      };
      readyHandlerRef.current = onMessage;
      window.addEventListener("message", onMessage);

      const iframe = iframeRef.current;
      if (!iframe) {
        setStatus("error");
        setErrorMessage("The 3D player failed to mount.");
        return;
      }

      iframe.src = "about:blank";
      bootTimeoutRef.current = setTimeout(() => {
        bootTimeoutRef.current = null;
        if (disposed || cancelledRef.current) return;
        iframe.src = `${WEBGL_BASE_URL}${WEBGL_ENTRY_PATH}`;
      }, BOOT_DELAY_MS);
    })();

    return () => {
      disposed = true;
      teardown();
    };
  }, [sourcePwoId, sceneName, authToken, localLanguage, sendToUnity, teardown]);

  // Unmount: stop Unity and drop every listener.
  useEffect(
    () => () => {
      cancelledRef.current = true;
      teardown();
      try {
        iframeRef.current?.contentWindow?.postMessage({ type: "APP_PAUSE" }, "*");
      } catch {}
      if (iframeRef.current) iframeRef.current.src = "about:blank";
      readyRef.current = false;
      bootedPwoRef.current = null;
    },
    [teardown]
  );

  // Bring the scene into view when it opens — it renders inline part-way down
  // the step, which on a long step can be below the fold.
  //
  // Web only: on web a View ref is the DOM node, elsewhere (and under
  // jest-expo) it is not, so the guard below turns this into a no-op rather
  // than a crash. That also means this is not covered by the test suite.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof node.scrollIntoView !== "function") return undefined;

    // One frame, so the 16:9 box has been laid out before we centre on it.
    const raf = requestAnimationFrame(() => {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        node.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const fitScale = containerWidth > 0 ? containerWidth / NATIVE_WIDTH : 1;

  return (
    <View
      ref={containerRef}
      style={[styles.container, { borderColor: isDarkMode ? "#1e293b" : "#cbd5e1" }]}
      onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}
    >
      <iframe
        title="lesson-pwo"
        ref={iframeRef}
        src="about:blank"
        allow="cross-origin; autoplay; fullscreen"
        scrolling="no"
        style={{
          border: "none",
          display: "block",
          // Absolute: a CSS transform does not shrink the layout box, so an
          // in-flow 1280x720 iframe would stretch the container to 720px tall
          // while the scaled picture only fills ~9/16 of it — the black band
          // across the bottom third. Out of flow, aspectRatio governs the box.
          position: "absolute",
          top: 0,
          left: 0,
          width: NATIVE_WIDTH,
          height: NATIVE_HEIGHT,
          transform: `scale(${fitScale})`,
          transformOrigin: "top left",
          backgroundColor: "#000",
        }}
      />

      {status !== "ready" && (
        <View style={styles.overlay} pointerEvents="none">
          {status === "error" ? (
            <>
              <MaterialCommunityIcons name="alert-circle-outline" size={28} color="#f87171" />
              <Text style={styles.overlayTitle}>
                {translate("This 3D scene could not be opened")}
              </Text>
              {errorMessage ? (
                <Text style={styles.overlayDetail}>{errorMessage}</Text>
              ) : null}
            </>
          ) : (
            <>
              <ActivityIndicator size="large" color="#38bdf8" />
              <Text style={styles.overlayTitle}>
                {translate("Loading 3D scene...")}
              </Text>
              <Text style={styles.overlayDetail}>
                {translate("First load can take up to a minute.")}
              </Text>
            </>
          )}
        </View>
      )}

      {onClose ? (
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={translate("Close 3D scene")}
          style={styles.close}
        >
          <MaterialCommunityIcons name="close" size={16} color="#ffffff" />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    // Big enough to work with, still centred and clearly inside the step.
    maxWidth: 1100,
    alignSelf: "center",
    aspectRatio: NATIVE_WIDTH / NATIVE_HEIGHT,
    borderRadius: 14,
    borderWidth: 1,
    overflow: "hidden",
    backgroundColor: "#000",
    marginBottom: 12,
    position: "relative",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    backgroundColor: "rgba(2,6,23,0.82)",
  },
  overlayTitle: {
    color: "#e2e8f0",
    fontSize: 14,
    fontWeight: "700",
    marginTop: 10,
    textAlign: "center",
  },
  overlayDetail: {
    color: "#94a3b8",
    fontSize: 12,
    marginTop: 4,
    textAlign: "center",
  },
  close: {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 20,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(15,23,42,0.75)",
  },
});

export default LessonPwoPlayer;
